'use client';

import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { useIsClient } from 'usehooks-ts';

import { Badge } from '@/components/ui/badge';

interface Timezone {
  name: string;
  label: string;
  flag: string;
}

function getLocaleString(date: Date, timeZone?: string) {
  // Using the following options to have this format: "YYYY-MM-DD, HH:mm"
  return date.toLocaleString('en-CA', {
    dateStyle: 'short',
    timeStyle: 'short',
    hour12: false,
    ...(timeZone && { timeZone }),
  });
}

interface TimeGroup {
  localeString: string;
  timezones: Timezone[];
}

export function getTimeGroups(date: Date, timezones: Timezone[]) {
  const timeGroups = timezones.reduce(
    (groups, timezone) => {
      const localeString = getLocaleString(date, timezone.name);

      if (!groups[localeString]) {
        groups[localeString] = {
          localeString,
          timezones: [],
        };
      }
      groups[localeString].timezones.push(timezone);

      return groups;
    },
    {} as Record<string, TimeGroup>,
  );

  const sortedTimeGroups = Object.values(timeGroups)
    .map((group) => ({
      ...group,
      timezones: group.timezones.sort((a, b) => a.label.localeCompare(b.label)),
    }))
    .sort((a, b) => a.localeString.localeCompare(b.localeString));

  return sortedTimeGroups;
}

export function WorldClock({
  dateString,
  timezones,
}: {
  dateString: string;
  timezones: Timezone[];
}) {
  const [userLocalTime, setUserLocalTime] = useState<string | null>(null);
  const isClient = useIsClient();

  const date = useMemo(() => new Date(dateString), [dateString]);

  useEffect(() => {
    const localTime = getLocaleString(date);
    setUserLocalTime(localTime);
  }, [date]);

  const timeGroups = getTimeGroups(date, timezones);

  return (
    <ol className="space-y-4">
      {timeGroups.map((timeGroup) => (
        <li key={timeGroup.localeString}>
          <WorldClockTimeGroup
            timeGroup={timeGroup}
            highlight={isClient && userLocalTime === timeGroup.localeString}
          />
        </li>
      ))}
    </ol>
  );
}

function WorldClockTimeGroup({
  timeGroup,
  highlight,
}: {
  timeGroup: TimeGroup;
  highlight?: boolean;
}) {
  const time = timeGroup.localeString.slice(12);

  return (
    <div className="flex flex-row gap-2">
      <div
        className={clsx(
          'w-[52px] min-w-[52px] border-r pr-2',
          highlight && 'border-primary decoration-primary',
        )}
      >
        <div className={clsx(highlight && 'underline')}>{time}</div>
        {highlight && <div className="text-primary/50 text-xs">tu hora</div>}
      </div>

      <ul className="flex flex-wrap gap-1">
        {timeGroup.timezones.map((timezone) => (
          <li key={timezone.label}>
            <Badge variant="secondary">
              {timezone.flag} {timezone.label}
            </Badge>
          </li>
        ))}
      </ul>
    </div>
  );
}
